import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

import { UsergroupTypeEnum } from 'app/interfaces';

interface StorageStreamType<T> {
    [id: number]: {
        isInitiated: boolean;
        subject: ReplaySubject<T>;
    };
    [key: string]: {
        isInitiated: boolean;
        subject: ReplaySubject<T>;
    };
}

@Injectable()
export class StorageService {
    // variables are called dynamically so linter can't see that they are used
    private lastUsedPosByGroupStreams: StorageStreamType<string> = {};
    private lastUsedPosStreams: StorageStreamType<number> = {};
    private registrationsEventStreams: StorageStreamType<number> = {};
    private singleEventRegistrationsEventStreams: StorageStreamType<number> = {};
    private transactionsPointOfSalesStreams: StorageStreamType<string> = {};
    private viewTypesStreams: StorageStreamType<number> = {};
    // tslint:enable:no-unused-variable

    getLastUsedPosByGroupStream(userHash: string, usergroupId: string): Observable<string> {
        const key = this.getJoinKey([userHash, usergroupId]);
        return this.getStream<string, typeof key>('lastUsedPosByGroupStreams', key);
    }

    getLastUsedPosStream(userHash: string): Observable<string> {
        return this.getStream<string, typeof userHash>('lastUsedPosStreams', userHash);
    }

    getRegistrationsEventsStream(usergroupId: string): Observable<number> {
        return this.getStream<number, typeof usergroupId>('registrationsEventStreams', usergroupId);
    }

    getSingleEventRegistrationsEventsStream(eventId: number): Observable<number> {
        return this.getStream<number, typeof eventId>('singleEventRegistrationsEventStreams', eventId);
    }

    getTransactionsPointOfSaleStream(usergroupId: string): Observable<string> {
        return this.getStream<string, typeof usergroupId>('transactionsPointOfSalesStreams', usergroupId);
    }

    getViewTypeStream(usergroupId: string): Observable<UsergroupTypeEnum> {
        return this.getStream<UsergroupTypeEnum, typeof usergroupId>('viewTypesStreams', usergroupId);
    }

    isLastUsedPosByGroupStreamInitiated(userHash: string, usergroupId: string): boolean {
        const key = this.getJoinKey([userHash, usergroupId]);
        return this.getIsInitiated<string, typeof key>('lastUsedPosByGroupStreams', key);
    }

    isLastUsedPosStreamInitiated(userHash: string): boolean {
        return this.getIsInitiated<string, typeof userHash>('lastUsedPosStreams', userHash);
    }

    isRegistrationsEventsStreamInitiated(usergroupId: string): boolean {
        return this.getIsInitiated<number, typeof usergroupId>('registrationsEventStreams', usergroupId);
    }

    isSingleEventRegistrationsEventsStreamInitiated(eventId: number): boolean {
        return this.getIsInitiated<number, typeof eventId>('singleEventRegistrationsEventStreams', eventId);
    }

    isTransactionPointOfSaleStreamInitiated(usergroupId: string): boolean {
        return this.getIsInitiated<string, typeof usergroupId>('transactionsPointOfSalesStreams', usergroupId);
    }

    isViewTypeStreamInitiated(usergroupId: string): boolean {
        return this.getIsInitiated<UsergroupTypeEnum, typeof usergroupId>('viewTypesStreams', usergroupId);
    }

    setLastUsedPosByGroup(userHash: string, usergroupId: string, posHash: string): void {
        const key = this.getJoinKey([userHash, usergroupId]);
        this.setLastUsedPos(userHash, posHash);
        this.setStream<typeof posHash, typeof key>('lastUsedPosByGroupStreams', key, posHash);
    }

    setLastUsedPos(userHash: string, posId: string): void {
        this.setStream<typeof posId, typeof userHash>('lastUsedPosStreams', userHash, posId);
    }

    setRegistrationsEvent(usergroupId: string, eventId: number): void {
        this.setStream<typeof eventId, typeof usergroupId>('registrationsEventStreams', usergroupId, eventId);
    }

    setSingleEventRegistrationsEvent(eventId: number, selectedEventId: number): void {
        this.setStream<typeof selectedEventId, typeof eventId>(
            'singleEventRegistrationsEventStreams',
            eventId,
            selectedEventId,
        );
    }

    setTransactionsPointOfSale(usergroupId: string, pointOfSaleId: string): void {
        this.setStream<typeof pointOfSaleId, typeof usergroupId>(
            'transactionsPointOfSalesStreams',
            usergroupId,
            pointOfSaleId,
        );
    }

    setViewType(usergroupId: string, usergroupType: UsergroupTypeEnum): void {
        this.setStream<typeof usergroupType, typeof usergroupId>('viewTypesStreams', usergroupId, usergroupType);
    }

    private createDefault<StreamType, IdentifierType>(name: string, identifier: IdentifierType): void {
        this.initStream<StreamType, IdentifierType>(name, identifier);
    }

    private getJoinKey(parts: Array<number | string>): string {
        return parts.join(';');
    }

    private getIsInitiated<StreamType, IdentifierType>(name: string, identifier: IdentifierType): boolean {
        this.createDefault<StreamType, IdentifierType>(name, identifier);
        return this[name][identifier].isInitiated;
    }

    private getStream<StreamType, IdentifierType>(name: string, identifier: IdentifierType): Observable<StreamType> {
        this.createDefault<StreamType, IdentifierType>(name, identifier);
        return this[name][identifier].subject.asObservable();
    }

    private initStream<StreamType, IdentifierType>(streamName: string, identifier: IdentifierType): void {
        if (!this[streamName].hasOwnProperty(identifier)) {
            this[streamName][identifier] = {
                isInitiated: false,
                subject: new ReplaySubject<StreamType>(1),
            };
        }
    }

    private setStream<StreamType, IdentifierType>(name: string, identifier: IdentifierType, value: StreamType): void {
        this.createDefault<StreamType, IdentifierType>(name, identifier);
        const streamContainer = this[name][identifier];
        streamContainer.isInitiated = true;
        streamContainer.subject.next(value);
    }
}
